import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, Send, Ban, History, ArrowUpRight, AlertTriangle, Wallet, Download, Eye, CreditCard, RefreshCw, Landmark, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { money, shortDate } from "@/lib/format";
import { Button } from "@/components/ui-kit/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui-kit/Card";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui-kit/Dialog";
import { StatusBadge } from "@/components/ui-kit/StatusBadge";
import { InvoiceBreakdown, fromSnapshot } from "@/components/invoices/InvoiceBreakdown";
import { PaymentDialog } from "@/components/payments/PaymentDialog";
import { LawPayRequestDialog } from "@/components/payments/LawPayRequestDialog";
import { AcsaSendDialog } from "@/components/invoices/AcsaSendDialog";
import { WriteOffDialog } from "@/components/invoices/WriteOffDialog";
import { PdfPreviewDialog } from "@/components/pdf/PdfPreviewDialog";
import { useFunder } from "@/funder/FunderContext";

function WrittenOffBadge() {
  return (
    <span data-testid="invoice-written-off-badge"
      className="inline-flex items-center gap-1 rounded-full border border-status-void/30 bg-status-void/10 px-2.5 py-0.5 text-xs font-medium text-status-void">
      <Ban className="h-3 w-3" /> Written off
    </span>
  );
}

function Meta({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="text-sm font-medium text-foreground">{children}</div>
    </div>
  );
}

export function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { payerLabel } = useFunder();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [lawpayOpen, setLawpayOpen] = useState(false);
  const [acsaOpen, setAcsaOpen] = useState(false);
  const [woOpen, setWoOpen] = useState(false);
  const [appendix, setAppendix] = useState(false);
  const [dl, setDl] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const load = () =>
    api.get(`/invoices/${invoiceId}`).then((r) => setInv(r.data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setAppendix(!!r.data.itemization_default)).catch(() => {});
  }, [invoiceId]);

  const downloadPdf = async () => {
    setDl(true);
    try { await downloadFile(`/invoices/${invoiceId}/pdf`, { appendix }); } finally { setDl(false); }
  };

  const act = async (path) => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/invoices/${invoiceId}/${path}`);
      setVoidOpen(false);
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const syncLawpay = async () => {
    setSyncing(true);
    setError("");
    setSyncMsg("");
    try {
      const r = await api.post(`/invoices/${invoiceId}/lawpay/sync`);
      setSyncMsg(r.data.synced > 0
        ? `Recorded ${r.data.synced} payment${r.data.synced > 1 ? "s" : ""} from LawPay.`
        : "Up to date — no new payments found on LawPay.");
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSyncing(false);
    }
  };

  if (!inv) {
    if (error) return <p className="text-sm text-destructive" data-testid="invoice-detail-error">{error}</p>;
    return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const isHeldDraft = inv.status === "Draft" && !inv.snapshot;
  const canVoid = inv.status !== "Void";
  const canLawPay = inv.payer === "Client" && inv.snapshot && ["Sent", "PartiallyPaid"].includes(inv.status);
  const canSendAcsa = inv.payer === "ACSA" && inv.snapshot && !["Void", "Superseded"].includes(inv.status);
  const canWriteOff = inv.snapshot && (["Sent", "PartiallyPaid"].includes(inv.status) || inv.write_off_status);

  const clearWriteOff = async () => {
    setBusy(true); setError("");
    try { await api.post(`/invoices/${invoiceId}/write-off`, { written_off: false }); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-invoices">
        <ArrowLeft className="h-4 w-4" /> All invoices
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground">{inv.number || "Draft invoice"}</h1>
            {inv.write_off_status ? <WrittenOffBadge /> : <StatusBadge status={inv.status} />}
          </div>
          <p className="mt-1.5 text-base font-semibold text-foreground" data-testid="invoice-header-client">{inv.client_name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground" data-testid="invoice-header-meta">{inv.matter_name} · {payerLabel(inv.payer)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {inv.snapshot && (
            <>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="appendix-toggle-label">
                <input type="checkbox" checked={appendix} onChange={(e) => setAppendix(e.target.checked)} data-testid="appendix-toggle" className="h-3.5 w-3.5 accent-primary" />
                Itemized appendix
              </label>
              <Button variant="secondary" onClick={() => setPreviewOpen(true)} data-testid="preview-invoice-pdf">
                <Eye className="h-4 w-4" /> Preview
              </Button>
              <Button variant="secondary" onClick={downloadPdf} disabled={dl} data-testid="download-invoice-pdf">
                {dl ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download PDF
              </Button>
            </>
          )}
          {inv.snapshot && inv.status !== "Void" && inv.status !== "Superseded" && (
            <Button onClick={() => setPayOpen(true)} data-testid="record-payment-button">
              <Wallet className="h-4 w-4" /> Record payment
            </Button>
          )}
          {canLawPay && (
            <Button onClick={() => setLawpayOpen(true)} data-testid="request-lawpay-button"
              className="bg-send text-send-foreground hover:bg-send/90 focus-visible:ring-send/40">
              <CreditCard className="h-4 w-4" /> Request payment via LawPay
            </Button>
          )}
          {canSendAcsa && (
            <Button onClick={() => setAcsaOpen(true)} data-testid="send-acsa-button"
              className="bg-status-paid text-white hover:bg-status-paid/90 focus-visible:ring-status-paid/40">
              <Landmark className="h-4 w-4" /> {inv.acsa_submitted_at ? "Resend to ACSA" : "Send to ACSA"}
            </Button>
          )}
          {isHeldDraft && (
            <Button variant="secondary" onClick={() => act("issue")} disabled={busy} data-testid="issue-draft-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Issue invoice
            </Button>
          )}
          {canVoid && (
            <Button variant="secondary" onClick={() => setVoidOpen(true)} disabled={busy} data-testid="void-button">
              <Ban className="h-4 w-4" /> Void
            </Button>
          )}
          {canWriteOff && (
            inv.write_off_status ? (
              <Button variant="secondary" onClick={clearWriteOff} disabled={busy} data-testid="undo-write-off-button">
                <RotateCcw className="h-4 w-4" /> Undo write-off
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => setWoOpen(true)} disabled={busy} data-testid="write-off-button">
                <Ban className="h-4 w-4" /> Mark written off
              </Button>
            )
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive" data-testid="invoice-action-error">{error}</p>}

      {inv.lawpay_sent_at && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/20 bg-primary/5 px-4 py-3 text-sm" data-testid="lawpay-sent-banner">
          <CreditCard className="h-4 w-4 text-primary" />
          <span className="text-foreground">Payment request sent via LawPay to <span className="font-medium">{inv.lawpay_sent_to}</span> on {shortDate(inv.lawpay_sent_at)}.</span>
          {inv.lawpay_payment_url && (
            <a href={inv.lawpay_payment_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="lawpay-banner-link">
              <ArrowUpRight className="h-4 w-4" /> Payment page
            </a>
          )}
          <Button variant="secondary" size="sm" className="ml-auto" onClick={syncLawpay} disabled={syncing} data-testid="lawpay-sync-button">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync from LawPay
          </Button>
        </div>
      )}
      {syncMsg && <p className="text-sm text-status-paid" data-testid="lawpay-sync-message">{syncMsg}</p>}

      {inv.acsa_submitted_at && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-status-paid/30 bg-status-paid/10 px-4 py-3 text-sm" data-testid="acsa-sent-banner">
          <Landmark className="h-4 w-4 text-status-paid" />
          <span className="text-foreground">Sent to ACSA{(inv.acsa_submitted_to || []).length ? <> — <span className="font-medium">{(inv.acsa_submitted_to || []).join(", ")}</span></> : ""} on {shortDate(inv.acsa_submitted_at)}.</span>
        </div>
      )}

      {inv.write_off_status && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-status-void/30 bg-status-void/10 px-4 py-3 text-sm" data-testid="write-off-banner">
          <Ban className="h-4 w-4 text-status-void" />
          <span className="text-foreground">
            Written off on {shortDate(inv.written_off_at)}{inv.write_off_reason ? <> — <span className="font-medium">{inv.write_off_reason}</span></> : ""}.
            <span className="text-muted-foreground"> Reporting flag only — the invoice amount is unchanged.</span>
          </span>
        </div>
      )}

      {(inv.supersedes_number || inv.superseded_by_number) && (
        <div className="flex flex-wrap gap-4 rounded-md border border-border bg-secondary/40 px-4 py-3 text-sm" data-testid="supersede-relationship">
          {inv.supersedes_number && (
            <span className="inline-flex items-center gap-1 text-muted-foreground"><ArrowUpRight className="h-4 w-4" /> Supersedes {inv.supersedes_number}</span>
          )}
          {inv.superseded_by_number && (
            <span className="inline-flex items-center gap-1 text-status-superseded">Superseded by {inv.superseded_by_number}</span>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]">
        <Card>
          <CardHeader><CardTitle>Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <Meta label="Number">{inv.number || "— (held draft)"}</Meta>
            <Meta label="Payer">{payerLabel(inv.payer)}</Meta>
            <Meta label="Issue date">{inv.issue_date ? shortDate(inv.issue_date) : "—"}</Meta>
            <Meta label="Status">{inv.write_off_status ? <WrittenOffBadge /> : <StatusBadge status={inv.status} />}</Meta>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Frozen breakdown</CardTitle>
            {inv.snapshot && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="frozen-note">
                <Lock className="h-3.5 w-3.5" /> Frozen at issue — settings changes won&apos;t move it
              </span>
            )}
          </CardHeader>
          <CardContent>
            {inv.snapshot ? (
              <InvoiceBreakdown payer={inv.payer} data={fromSnapshot(inv.snapshot)} />
            ) : (
              <p className="py-4 text-sm text-muted-foreground">This draft hasn&apos;t been issued yet — issue it to assign a number and freeze a snapshot.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-2">
          <History className="h-4 w-4 text-muted-foreground" />
          <CardTitle>Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(inv.audit || []).length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">No activity recorded.</p>
          ) : (
            inv.audit.map((a) => (
              <div key={a.id} className="border-l-2 border-border pl-3" data-testid={`detail-audit-${a.id}`}>
                <p className="text-sm text-foreground">{a.message}</p>
                <p className="text-xs text-muted-foreground">{shortDate(a.timestamp)} · {a.actor || "system"}</p>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Dialog open={voidOpen} onOpenChange={(o) => { if (!busy) setVoidOpen(o); }}>
        <DialogContent data-testid="void-confirm-dialog">
          <DialogTitle className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-status-void/10 text-status-void"><AlertTriangle className="h-4 w-4" /></span>
            Void {inv.number || "this draft"}?
          </DialogTitle>
          <DialogDescription>
            {inv.status === "Paid"
              ? "This invoice is marked Paid. Voiding it is a consequential financial action and will be recorded in the append-only audit log. This cannot be undone."
              : "Voiding marks the invoice as Void and records it in the append-only audit log. This cannot be undone."}
          </DialogDescription>
          {error && <p className="mt-3 text-sm text-destructive" data-testid="void-dialog-error">{error}</p>}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setVoidOpen(false)} disabled={busy} data-testid="void-cancel-button">Cancel</Button>
            <Button onClick={() => act("void")} disabled={busy} data-testid="void-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Void invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        matterId={inv.matter_id}
        clientId={inv.client_id}
        defaultPayer={inv.payer}
        invoiceId={inv.id}
        onDone={load}
      />

      <LawPayRequestDialog
        open={lawpayOpen}
        onOpenChange={setLawpayOpen}
        invoice={inv}
        onDone={load}
      />

      <AcsaSendDialog
        open={acsaOpen}
        onOpenChange={setAcsaOpen}
        invoice={inv}
        onDone={load}
      />

      <WriteOffDialog
        open={woOpen}
        onOpenChange={setWoOpen}
        invoice={{ id: inv.id, number: inv.number, balance_due: inv.amount_due_now ?? inv.snapshot?.amount_due, write_off_reason: inv.write_off_reason }}
        onDone={load}
      />

      {inv.snapshot && (
        <PdfPreviewDialog
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          path={`/invoices/${invoiceId}/pdf`}
          params={{ appendix }}
          title="Invoice preview"
        />
      )}
    </div>
  );
}
